import { CheckCircle, FiberManualRecordOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import React from "react";

// Keep static structures outside the component
const steps = [
    { name: "Order Placed", description: "Waiting for verification", value: "PENDING" },
    { name: "Confirmed", description: "Order has been accepted", value: "PLACED" },
    { name: "Packed", description: "Item Packed in Dispatch Warehouse", value: "CONFIRMED" },
    { name: "Shipped", description: "On the way to delivery partner", value: "SHIPPED" },
    { name: "Arrived", description: "Item successfully delivered", value: "DELIVERED" },
];

const canceledSteps = [
    { name: "Order Placed", description: "Pending processing", value: "PENDING" },
    { name: "Order Cancelled", description: "This order has been cancelled", value: "CANCELLED" },
];

interface OrderStepperProps {
    orderStatus: string | undefined | null;
}

const OrderStepper = ({ orderStatus }: OrderStepperProps) => {
    // 🟢 Guard: If status isn't available yet, show loader instantly
    if (!orderStatus) {
        return <div className="text-gray-500 text-center my-10 font-medium">Loading tracking details...</div>;
    }

    const normalizedStatus = orderStatus.toUpperCase().trim();

    // 🟢 Derived State: Pick the steps dynamically without triggering asynchronous state delays
    const activeStepsList = normalizedStatus === "CANCELLED" ? canceledSteps : steps;

    // Find index using normalized uppercase strings
    const foundIndex = activeStepsList.findIndex(
        (step) => step.value.toUpperCase() === normalizedStatus
    );
    
    // If the backend returns an unrecognized status, default to step 0
    const activeStepIndex = foundIndex !== -1 ? foundIndex : 0;

    return (
        <Box className="my-10">
            {activeStepsList.map((step, index) => {
                const isStepCompletedOrActive = index <= activeStepIndex;
                const isStepActive = step.value.toUpperCase() === normalizedStatus;

                return (
                    <div key={step.value} className="flex px-4">
                        {/* Left Side Icon and Connector Line */}
                        <div className="flex flex-col items-center">
                            <Box 
                                className={`w-8 h-8 rounded-full flex items-center justify-center z-10 transition-colors duration-300 ${
                                    isStepCompletedOrActive
                                        ? "bg-teal-50 text-teal-500 border border-teal-500"
                                        : "bg-gray-100 text-gray-400 border border-gray-300"
                                }`}
                            >
                                {isStepActive ? (
                                    <CheckCircle className="text-teal-500" />
                                ) : (
                                    <FiberManualRecordOutlined className="text-xs" />
                                )}
                            </Box>
                            
                            {/* Vertical connecting line */}
                            {activeStepsList.length - 1 !== index && (
                                <div 
                                    className={`h-20 w-[2px] transition-colors duration-300 ${
                                        index < activeStepIndex
                                            ? "bg-teal-500" 
                                            : "bg-gray-300"
                                    }`}
                                ></div>
                            )}
                        </div>

                        {/* Right Side Status Details */}
                        <div className="ml-4 w-full">
                            <div
                                className={`p-3 rounded-md transition-all duration-300 ${
                                    isStepActive
                                        ? normalizedStatus === "CANCELLED"
                                            ? "bg-red-500 text-white -translate-y-2 shadow-md"
                                            : "bg-teal-600 text-white -translate-y-2 shadow-md"
                                        : ""
                                } w-full`}
                            >
                                <p className={`font-semibold ${isStepActive ? "text-white" : "text-gray-800"}`}>
                                    {step.name}
                                </p>

                                <p
                                    className={`text-xs ${
                                        isStepActive
                                            ? "text-teal-100"
                                            : "text-gray-500"
                                    }`}
                                >
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </Box>
    );
};

export default OrderStepper;